<template>
  <div class="formgrid grid vertical-container">
    <div class="bound" v-if="isDrag || isResize">
      <div class="svg-container">
        <svg :width="width" :height="height - 245" xmlns="http://www.w3.org/2000/svg"
          xmlns:svg="http://www.w3.org/2000/svg">
          <g class="layer">
            <title>bound</title>
            <rect fill="none" :height="height - 282" id="svg_2" stroke="var(--text-color-secondary)" stroke-width="2"
              :width="width - 436" x="8" y="2" />
          </g>
        </svg>
      </div>
    </div>
    <div class="col-12">
      <div class="design-facility" id="design-facility" ref="designFacilityRef" :style="style">
        <ComponentPanel v-if="showComponentPanel" @close="store.commit('SET_SHOW_COMPONENT_PANEL', false)"
          :moveable="editMode" />
        <Facility :scriptId="scriptId" :defaultScript="defaultScript" />
        <InspectorPanel v-if="editMode" :value="current" @update:value="updateCurrent"
          :editing="editMode && moveableItem !== null" :moveableItem="moveableItem" :facilityId="facilityId" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onBeforeUnmount, onUnmounted, defineProps } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import Facility from "./components/Facility.vue";
import InspectorPanel from "./InspectorPanel.vue";
import ComponentPanel from "./ComponentPanel.vue";
import MoveableHistoryManager from "./utils/MoveableHistoryManager.js";
import { MAX_WIDTH, MAX_HEIGHT, MIN_WIDTH, MIN_HEIGHT, REVERS_OPERATION, OPERATION_TYPE } from "./utils/constants.js";
import Moveable from "moveable";
import EventBus from "@/core/EventBus";
import KeyController from "keycon";

const props = defineProps({
  scriptId: { type: Number, default: null },
  interception: { type: Boolean, default: false },
  defaultScript: { type: Object, default: null },
});

const store = useStore();
const route = useRoute();

const designFacilityRef = ref(null);
const style = ref({ "font-size": "12px" });
const moveableItems = ref([]);
const timeout = ref({});
const countDrag = ref(0);
const isRequiredSave = ref(false);
const moveableItem = ref(null);
const isDrag = ref(false);
const isResize = ref(false);
const current = reactive({ x: 0, y: 0, width: 0, height: 0, id: "", PropertyURI: "" });
const manager = ref(null);
const startLeft = ref(null);
const startTop = ref(null);
const startWidth = ref(null);
const startHeight = ref(null);

const showComponentPanel = computed(() => store.getters.showComponentPanel);
const editMode = computed(() => store.getters.editMode);
const facilityId = computed(() => route.params.facility_id);

const width = computed(() => window ? window.screen.width : 1800);
const height = computed(() => window ? window.screen.height : 800);
const validatePosisition = computed(() =>
  current.y > MIN_HEIGHT &&
  current.y + current.height < MAX_HEIGHT &&
  current.x > MIN_WIDTH &&
  current.x + current.width < MAX_WIDTH
);

manager.value = new MoveableHistoryManager();

function isDragging() {
  if (!moveableItem.value) return false;
  return moveableItem.value.innerMoveable.moveable.moveable.isDragging();
}

function setCurrentPos(elem) {
  current.id = elem.id;
  current.x = Number(elem.style.left.substr(0, elem.style.left.length - 2));
  current.y = Number(elem.style.top.substr(0, elem.style.top.length - 2));
  manager.value.setMoveable({ ...current }, validatePosisition.value);
}

function getElemId(elem) {
  do {
    if (elem.parentNode) elem = elem.parentNode;
    else { elem = null; break; }
  } while (!elem.id);
  return elem;
}

function deleteComponentById(id) {
  EventBus.emit("on-delete-component", id);
  if (moveableItem.value && moveableItem.value.id === id) {
    moveableItem.value.destroy();
    moveableItem.value = null;
    current.x = 0;
    current.y = 0;
    current.width = 0;
    current.height = 0;
    current.id = "";
    current.PropertyURI = "";
    moveableItems.value = [];
  }
}

function deleteItem() {
  if (moveableItem.value && moveableItem.value.id) {
    const id = moveableItem.value.id;
    manager.value.setMoveable({ ...current }, true, OPERATION_TYPE.delete);
    deleteComponentById(id);
  } else {
    store.commit("SET_ERROR", { Er: "Не удалось удалить элемент. Идентификатор объекта не найден. Обновите страницу и попробуйте снова." });
  }
}

const DeleteViewable = {
  name: "DeleteViewable",
  props: {},
  events: {},
  render: (moveable, render) => {
    const rect = moveable.getRect();
    const styleObj = {
      position: "absolute",
      top: `${-23}px`,
      left: `${rect.width + 16}px`,
      borderRadius: "5px",
      background: validatePosisition.value ? "#44AAFF" : "#ff0000",
      padding: "0px 7px 3px 7px",
      color: "white",
      fontSize: "13px",
      whiteSpace: "nowrap",
      fontWeight: "500",
      willChange: "transform",
      transform: "translate(-50%, 0px)",
    };
    const propsObj = {
      style: styleObj,
      key: "delete-viewer",
      class: "moveable-delete",
      onClick: () => deleteItem(),
    };
    const text = `x`;
    current.width = rect.width;
    current.height = rect.height;
    return render.createElement("div", propsObj, [text]);
  },
};

function createMoveableComponent(elem, parent, elementGuidelines) {
  if (moveableItems.value.findIndex((item) => item.id == elem.id) > -1) return;
  if (moveableItem.value) {
    moveableItem.value.destroy();
    moveableItems.value = [];
    moveableItem.value = null;
  }

  const moveableInstance = new Moveable(parent, {
    target: elem,
    elementGuidelines: elementGuidelines,
    container: designFacilityRef.value,
    draggable: true,
    resizable: true,
    ables: [DeleteViewable],
    props: { DeleteViewable: true },
    zoom: 1,
    origin: true,
    keepRatio: false,
    edge: false,
    pinchable: true,
    snappable: true,
    snapThreshold: 5,
    isDisplaySnapDigit: true,
    snapGap: true,
    snapDirections: { top: true, right: true, bottom: true, left: true },
    elementSnapDirections: { top: true, right: true, bottom: true, left: true },
    snapDigit: 0,
  });

  let frame = { translate: [0, 0] };

  moveableInstance
    .on("resizeStart", (e) => {
      startLeft.value = Number(e.target.style.left.slice(0, -2));
      startTop.value = Number(e.target.style.top.slice(0, -2));
      startWidth.value = Number(e.target.style.width.slice(0, -2));
      startHeight.value = Number(e.target.style.height.slice(0, -2));
      e.target.style.willChange = "left, top, width, height";
      manager.value.setMoveable({ ...current }, validatePosisition.value);
      e.setOrigin(["%", "%"]);
      e.dragStart && e.dragStart.set(frame.translate);
    })
    .on("resize", (e) => {
      const beforeTranslate = e.drag.beforeTranslate;
      frame.translate = beforeTranslate;
      e.target.style.width = `${e.width}px`;
      e.target.style.height = `${e.height}px`;
      const offsetX = e.direction[0] === -1 ? beforeTranslate[0] : 0;
      const offsetY = e.direction[1] === -1 ? beforeTranslate[1] : 0;
      e.target.style.left = `${startLeft.value + offsetX}px`;
      e.target.style.top = `${startTop.value + offsetY}px`;
      current.x = startLeft.value + offsetX;
      current.y = startTop.value + offsetY;
      isResize.value = true;
    })
    .on("resizeEnd", () => {
      const currentCopy = { ...current, id: elem.id };
      manager.value.setMoveable({ ...current }, validatePosisition.value);
      if (!validatePosisition.value) {
        let last = manager.value.getPreviousMoveable(true);
        if (!last) last = manager.value.getCurrentMoveable(true);
        if (last) revertResizable(last);
      }
      if (isResize.value) {
        EventBus.emit("on-update-component", currentCopy);
        isResize.value = false;
      }
    })
    .on("drag", ({ target, left, top }) => {
      target.style.left = `${left}px`;
      target.style.top = `${top}px`;
      current.x = left;
      current.y = top;
      isDrag.value = true;
    })
    .on("dragEnd", () => {
      const currentCopy = { ...current, id: elem.id };
      manager.value.setMoveable({ ...current }, validatePosisition.value);
      if (!validatePosisition.value) {
        let last = manager.value.getPreviousMoveable(true);
        if (!last) last = manager.value.getCurrentMoveable(true);
        if (last) revertMoveable(last);
      }
      if (isDrag.value) {
        countDrag.value++;
        EventBus.emit("on-update-component", currentCopy);
        isDrag.value = false;
      }
    })
    .on("dragStart", ({ target }) => setCurrentPos(target));

  moveableInstance.id = elem.id;
  setCurrentPos(elem);
  moveableItem.value = moveableInstance;
  moveableItems.value.push(moveableInstance);
}

function onMoveable(id, elementGuidelines) {
  const elem = document.getElementById(id);
  if (!elem?.id) {
    store.commit("SET_ERROR", {
      er: "Не удалось выделить элемент. Идентификатор объекта не найден. Попробуйте выбрать элемент заново.",
    });
    return;
  }
  const elemParent = elem.parentNode;
  const elemParentId = getElemId(elemParent);
  if (elemParentId) {
    createMoveableComponent(elem, elemParentId, elementGuidelines);
  }
}

function updateCurrent(newData) {
  Object.keys(current).forEach(key => delete current[key]);
  Object.assign(current, newData);
}

function onSelectComponent(element) {
  if (editMode.value) {
    onMoveable(element.id, element.elementGuidelines);
    const currentData = { id: element.id, ...element.attrList, ...element.attrList?.attrList };
    delete currentData.attrList?.attrList;
    updateCurrent(currentData);
  }
}


function onEditMoveable() {
  store.commit("SET_EDIT_MODE", !editMode.value);
  if (!editMode.value && moveableItem.value) {
    moveableItem.value.destroy();
    moveableItem.value = null;
    current.x = 0;
    current.y = 0;
    current.width = 0;
    current.height = 0;
    current.id = "";
    current.PropertyURI = "";
  }
  if (isRequiredSave.value && !editMode.value) {
    EventBus.emit("on-load-form-script", moveableItem.value);
  }
  store.commit("SET_SHOW_COMPONENT_PANEL", editMode.value);
  store.dispatch("setScriptProcess", !editMode.value);
  moveableItems.value = [];
}



function revertMoveable(last) {
  timeout.value.revertMoveable = setTimeout(() => {
    if (moveableItem.value && !isDragging()) {
      let deltaY = 0, deltaX = 0;
      if (current.y <= MIN_HEIGHT) {
        deltaY = Math.abs(current.y - last.y);
        deltaY = deltaY < 20 ? 30 : deltaY;
      }
      if (current.y + current.height >= MAX_HEIGHT) {
        deltaY = Math.abs(current.y) * -1 - Math.abs(last.y) * -1;
        deltaY = deltaY > -20 ? -30 : deltaY;
      }
      if (current.x <= MIN_WIDTH) {
        deltaX = Math.abs(current.x) + Math.abs(last.x);
        deltaX = deltaX < 20 ? 30 : deltaX;
      }
      if (current.x + current.width >= MAX_WIDTH) {
        deltaX = Math.abs(current.x) - Math.abs(last.x);
        if (deltaX < 20 && deltaX != 0) deltaX = 30;
        deltaX = deltaX * -1;
      }
      moveableItem.value.request("draggable", {
        deltaX, deltaY, isInstant: true,
      });
    }
  }, 40);
}

function revertResizable(last) {
  timeout.value.revertResizable = setTimeout(() => {
    if (moveableItem.value && !isDragging()) {
      let deltaHeight = 0, deltaWidth = 0;
      if (current.y + current.height >= MAX_HEIGHT) {
        deltaHeight = (Math.abs(current.height) - Math.abs(last.height)) * -1;
        if (deltaHeight > -5) deltaHeight = -10;
      }
      if (current.x + current.width >= MAX_WIDTH) {
        deltaWidth = (Math.abs(current.width) - Math.abs(last.width)) * -1;
        if (deltaWidth > -5) deltaWidth = -10;
      }
      moveableItem.value.request("resizable", {
        deltaWidth, deltaHeight, isInstant: true,
      });
    }
  }, 40);
}

function revert(prev, operation) {
  const action = operation === 'undo' ? prev.operationType : REVERS_OPERATION[prev.operationType];
  switch (action) {
    case OPERATION_TYPE.transform:
      switchToAnotherElement(prev);
      revertTransform(prev);
      break;
    case OPERATION_TYPE.insert:
      switchToAnotherElement(prev);
      revertInsert(prev);
      break;
    case OPERATION_TYPE.delete:
      revertDelete(prev);
      break;
  }
}

function revertDelete(prev) {
  const component = {
    attrList: { ...prev },
    typeName: prev.id.replace(/[^a-z,_]/gi, ""),
    id: parseInt(prev.id.match(/\d+/)),
  };
  delete component.attrList.id;
  delete component.attrList.operationType;
  EventBus.emit("on-recovery-component", component);
}

function revertInsert(prev) {
  deleteComponentById(prev.id);
}

function switchToAnotherElement(prev) {
  if (current && prev && current.id !== prev.id) {
    const element = {
      id: parseInt(prev.id.match(/\d+/)),
      typeName: prev.id.replace(/[^a-z,_]/gi, ""),
      attrList: {},
    };
    EventBus.emit("on-target-component", element);
    timeout.value.switchToAnotherElement = setTimeout(() => revert(prev, 'реверт вызвал сам себя'), 10);
  }
}

function revertTransform(prev) {
  let deltaHeight = 0, deltaWidth = 0, deltaY = 0, deltaX = 0;
  if (current?.id === prev?.id) {
    deltaHeight = (Math.abs(current.height) - Math.abs(prev.height)) * -1;
    deltaWidth = (Math.abs(current.width) - Math.abs(prev.width)) * -1;
    deltaY = (Math.abs(current.y) - Math.abs(prev.y)) * -1;
    deltaX = (Math.abs(current.x) - Math.abs(prev.x)) * -1;
  }
  timeout.value.revertTransform = setTimeout(() => {
    if (moveableItem.value) {
      if (deltaHeight || deltaWidth) {
        moveableItem.value.request("resizable", {
          deltaWidth, deltaHeight, isInstant: true,
        });
      }
      if (deltaY || deltaX) {
        moveableItem.value.request("draggable", {
          deltaX, deltaY, isInstant: true,
        });
      }
    }
  }, 5);
}

onMounted(() => {
  store.commit("SET_EDIT_MODE", false);
  style.value = {
    "font-size": (localStorage.scale ? localStorage.scale : 13) - 1.4 + "px",
  };

  KeyController.global
    .keydown("left", ({ inputEvent }) => {
      if (moveableItem.value && !isDragging() && inputEvent.target.value === undefined) {
        moveableItem.value.request("draggable", { deltaX: -1, isInstant: true });
        inputEvent.preventDefault();
      }
    })
    .keydown("up", ({ inputEvent }) => {
      if (moveableItem.value && !isDragging() && inputEvent.target.value === undefined) {
        moveableItem.value.request("draggable", { deltaY: -1, isInstant: true });
        inputEvent.preventDefault();
      }
    })
    .keydown("right", ({ inputEvent }) => {
      if (moveableItem.value && !isDragging() && inputEvent.target.value === undefined) {
        moveableItem.value.request("draggable", { deltaX: 1, isInstant: true });
        inputEvent.preventDefault();
      }
    })
    .keydown("down", ({ inputEvent }) => {
      if (moveableItem.value && !isDragging() && inputEvent.target.value === undefined) {
        moveableItem.value.request("draggable", { deltaY: 1, isInstant: true });
        inputEvent.preventDefault();
      }
    })
    .keydown("delete", ({ inputEvent }) => {
      if (moveableItem.value && !isDragging() && inputEvent.target.value === undefined) {
        deleteItem();
        inputEvent.preventDefault();
      }
    })
    .keydown(["ctrl", "z"], ({ inputEvent }) => {
      if (editMode.value && props.interception && inputEvent.target.value === undefined) {
        const prev = manager.value.getUndoMoveable(true);
        if (prev) revert(prev, 'undo');
        inputEvent.preventDefault();
      }
    })
    .keydown(["ctrl", "c"], ({ inputEvent }) => {
      if (editMode.value && props.interception && inputEvent.target.value === undefined) {
        EventBus.emit("on-get-component-copy", current.id);
        inputEvent.preventDefault();
      }
    })
    .keydown(["ctrl", "v"], ({ inputEvent }) => {
      if (editMode.value && props.interception && inputEvent.target.value === undefined) {
        const element = manager.value.getCopyMoveable();
        if (element) EventBus.emit("on-create-component-select", element);
        inputEvent.preventDefault();
      }
    })
    .keydown(["ctrl", "y"], ({ inputEvent }) => {
      if (editMode.value && props.interception && inputEvent.target.value === undefined) {
        const rollback = manager.value.getRedoMoveable(true);
        if (rollback) revert(rollback, 'redo');
        inputEvent.preventDefault();
      }
    });

  EventBus.on("on-click-component", (element) => onSelectComponent(element));
  EventBus.on("on-set-component-copy", (element) => manager.value.setCopyMoveable(element));
  EventBus.on("on-edit-moveable", () => onEditMoveable());
  EventBus.on("on-change-save", (val = true) => isRequiredSave.value = val);
  EventBus.on("on-destroy-moveable", () => { });
  EventBus.on("on-insert-moveable", (component) => {
    const moveable = {
      ...component.attrList,
      PropertyURI: component.attrList?.PropertyURI,
      height: component.attrList.height,
      width: component.attrList.width,
      x: component.attrList.x,
      y: component.attrList.y,
      id: component.typeName + component.id,
    };
    manager.value.setMoveable(moveable, true, OPERATION_TYPE.insert);
  });
});

onBeforeUnmount(() => {
  if (moveableItem.value) {
    moveableItem.value.destroy();
    moveableItem.value = null;
    current.x = 0;
    current.y = 0;
    current.width = 0;
    current.height = 0;
    current.id = "";
    current.PropertyURI = "";
  }
  EventBus.off("on-click-component");
  EventBus.off("on-edit-moveable");
  EventBus.off("on-change-save");
  EventBus.off("on-destroy-moveable");
  EventBus.off("on-insert-moveable");
  EventBus.off("on-set-component-copy");

  if (isRequiredSave.value && confirm("Есть не сохраненные данные! Сохранить?")) {
    EventBus.emit("on-save-facility");
  }
});

onUnmounted(() => {
  KeyController.global.offKeydown("left");
  KeyController.global.offKeydown("up");
  KeyController.global.offKeydown("right");
  KeyController.global.offKeydown("down");
  KeyController.global.offKeydown("delete");
  KeyController.global.offKeydown(["ctrl", "z"]);
  KeyController.global.offKeydown(["ctrl", "c"]);
  KeyController.global.offKeydown(["ctrl", "v"]);
  KeyController.global.offKeydown(["ctrl", "y"]);
  clearTimeout(timeout.value.revertMoveable);
  clearTimeout(timeout.value.revertResizable);
  clearTimeout(timeout.value.switchToAnotherElement);
  clearTimeout(timeout.value.revertTransform);
});
</script>

<style lang="scss" scoped>
.design-facility {
  font-family: var(--font-family); //Tahoma, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  /* text-align: center; */
  color: #2c3e50;
}

.scrolls {
  overflow-x: scroll;
  white-space: nowrap;
}

.bound {
  position: relative;
}

.svg-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
</style>